import { useEffect, useRef } from "react";

export interface HotkeyOptions {
  enabled?: boolean;
  preventDefault?: boolean;
  allowInInputs?: boolean;
  target?: HTMLElement | Document | null;
}

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

export const modKeyLabel = isMac ? "⌘" : "Ctrl";

function matches(e: KeyboardEvent, combo: string): boolean {
  const parts = combo.toLowerCase().split("+");
  const key = parts.pop() ?? "";
  const mod = parts.includes("mod");
  const ctrl = parts.includes("ctrl") || (mod && !isMac);
  const meta = parts.includes("meta") || (mod && isMac);
  const shift = parts.includes("shift");
  const alt = parts.includes("alt");
  if (e.ctrlKey !== ctrl || e.metaKey !== meta || e.shiftKey !== shift || e.altKey !== alt) return false;
  const k = e.key.toLowerCase();
  return k === key || (key === "esc" && k === "escape") || (key === "space" && k === " ");
}

function inEditable(t: EventTarget | null): boolean {
  if (!(t instanceof HTMLElement)) return false;
  const tag = t.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || t.isContentEditable;
}

export function useHotkey(combo: string | string[], handler: (e: KeyboardEvent) => void, opts: HotkeyOptions = {}): void {
  const { enabled = true, preventDefault = true, allowInInputs = false, target } = opts;
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);
  useEffect(() => {
    if (!enabled) return;
    const combos = Array.isArray(combo) ? combo : [combo];
    const el: HTMLElement | Document = target ?? document;
    const onKey = (ev: Event) => {
      const e = ev as KeyboardEvent;
      if (!combos.some((c) => matches(e, c))) return;
      const hasMod = e.ctrlKey || e.metaKey;
      if (!allowInInputs && !hasMod && inEditable(e.target)) return;
      if (preventDefault) e.preventDefault();
      handlerRef.current(e);
    };
    el.addEventListener("keydown", onKey);
    return () => el.removeEventListener("keydown", onKey);
  }, [combo, enabled, preventDefault, allowInInputs, target]);
}
